import { EngagementList } from './../../../query-interface/models/query-interface';
import { Component, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Engagements, EngagementStatus, GetEngagementsQuery, GetEngagementsQueryVariables } from '../../../../API';
import { GetEngagements } from '../../../../graphql/queries';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { AppService } from '../../services/app.service';
import { ApiService } from '../../services/api.service';
import { UserManagementService } from '../../services/user-management.service';
import { AuthService } from '../../services/auth.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  engagementList?: EngagementList[];
  activeEngagement: UntypedFormControl = new FormControl('');
  masterEngagementList: Engagements[] = [];
  userEmail?: string;
  userOrgType?: string;
  orgTypeColor: string = '#d50a0a';
  isLoading = false;
  previousEngagementValue: any;
  userRole?: string;
  selectedEngagement: Engagements | null = null;

  constructor(
    public appService: AppService,
    private nzMessageService: NzMessageService,
    private apiService: ApiService,
    private authService: AuthService,
    private userManagementService: UserManagementService,
    private modal: NzModalService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.userEmail = this.userManagementService.getEmail();
    this.userOrgType = this.userManagementService.getOrgType();
    this.userRole = this.userManagementService.getUserRole();
    this.getEngagementList();
    this.activeEngagement.valueChanges.subscribe((newValue) => this.onEngagementValueChange(newValue));
    this.appService.triggerEngagement$.subscribe(() => {
      this.getEngagementList(true);
    });
  }

  private onEngagementValueChange(newValue: any) {
    this.updateSelectedEngagement(newValue);

    if (this.previousEngagementValue) {
      this.modal.create({
        nzTitle: 'Confirmation To Change Engagement',
        nzContent: `Are you sure you want to change from "${this.previousEngagementValue}" to "${newValue}"? 
          This action will result in the loss of all unsaved information and will redirect you to the homepage.`,
        nzOnOk: () => {
          this.previousEngagementValue = newValue;
          this.appService.activeEngagementSubject$.next(this.selectedEngagement);
          this.setEngagementStatus();
          this.router.navigate(['/']);
        },
        nzOnCancel: () => this.activeEngagement.setValue(this.previousEngagementValue, { emitEvent: false }),
      });
    } else {
      this.appService.activeEngagementSubject$.next(this.selectedEngagement);
      this.setEngagementStatus();
    }
  }

  getEngagementList(isRefresh: boolean = false) {
    this.isLoading = true;
    this.apiService
      .query<GetEngagementsQuery, GetEngagementsQueryVariables>(GetEngagements, {
        organizationType: this.userManagementService.getOrgType(),
      })
      .then((result) => {
        if (result.errors?.length) {
          this.nzMessageService.error('Engagement list is not available');
          return;
        }
        const engagements = (result.data?.GetEngagements as Engagements[]) || [];
        this.masterEngagementList = engagements;
        this.engagementList = this.generateEngagementList(engagements);

        if (isRefresh) {
          this.updateSelectedEngagement(this.selectedEngagement?.engagement as string);
          this.setEngagementStatus();
          this.appService.activeEngagementSubject$.next(this.selectedEngagement);
          return;
        }

        this.activeEngagement.setValue(this.engagementList[0]?.value);
        this.previousEngagementValue = this.engagementList[0]?.value;
      })
      .catch((error) => {
        const errorType = error?.errors?.[0]?.errorType;
        if (errorType !== 'EngagementsNotFound') {
          this.appService.showEngagements &&
            this.nzMessageService.error(
              `Error while fetching engagement list: ${JSON.stringify(error.errors.message)}`,
            );
        }
      })
      .finally(() => (this.isLoading = false));
  }

  updateSelectedEngagement(engagementValue: string) {
    this.selectedEngagement =
      this.masterEngagementList.find((engagement) => engagement.engagement === engagementValue) ?? null;
  }

  setEngagementStatus(): void {
    this.appService.isEngagementActive = this.selectedEngagement?.status === EngagementStatus.ACTIVE;
  }

  getStatusColor(status: EngagementStatus): string {
    switch (status) {
      case EngagementStatus.ACTIVE:
        return 'success';
      case EngagementStatus.ON_HOLD:
        return 'error';
      default:
        return 'default';
    }
  }

  getStatusDisplayName(status: EngagementStatus): string {
    switch (status) {
      case EngagementStatus.ACTIVE:
        return 'Active';
      case EngagementStatus.ON_HOLD:
        return 'Inactive';
      default:
        return status;
    }
  }

  generateEngagementList(list: Engagements[]): EngagementList[] {
    return list.map((engagement) => {
      const value = engagement.engagement;
      const status = engagement.status as EngagementStatus;
      const name = value.replace('-', ' x '); // Replace "-" with " x "
      return {
        value,
        name,
        status,
      };
    });
  }

  logout(): void {
    this.authService.logout();
  }
}
